// A password field with show/hide (FR-013, FR-047, FR-048): the toggle is a 44 × 44 button named
// "Show password" / "Hide password", pressed state exposed. The value is never pre-filled.
import { forwardRef, useState } from "react";
import { Platform, StyleSheet, type TextInput } from "react-native";

import { makeStyles } from "@/theme/makeStyles";

import { PressableTarget } from "./PressableTarget";
import { Text } from "./Text";
import { TextField, type TextFieldProps } from "./TextField";

export type PasswordFieldProps = Omit<
  TextFieldProps,
  "kind" | "secureTextEntry" | "accessory"
> & {
  kind?: "current-password" | "new-password";
  showLabel: string;
  hideLabel: string;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({ toggle: { paddingHorizontal: t.space.md } }),
);

export const PasswordField = forwardRef<TextInput, PasswordFieldProps>(
  function PasswordField(
    { kind = "current-password", showLabel, hideLabel, ...rest },
    ref,
  ) {
    const styles = useStyles();
    const [visible, setVisible] = useState(false);
    const pressed =
      Platform.OS === "web"
        ? ({ "aria-pressed": visible } as object)
        : { accessibilityState: { selected: visible } };
    return (
      <TextField
        ref={ref}
        {...rest}
        kind={kind}
        secureTextEntry={!visible}
        accessory={
          <PressableTarget
            accessibilityLabel={visible ? hideLabel : showLabel}
            onPress={() => setVisible((v) => !v)}
            style={styles.toggle}
            {...pressed}
          >
            <Text tone="primary" weight="semiBold" size="sm">
              {visible ? "Hide" : "Show"}
            </Text>
          </PressableTarget>
        }
      />
    );
  },
);
